import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import Api from "../Api/Api.json";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { useComplaintsData } from "../hooks/useComplaintsData";

let audio = null;

const ComplaintsList = ({ patient_id }) => {
  const { data, isLoading, isError, error } = useComplaintsData(patient_id);
  const [audioPath, setAudioPath] = useState("");
  const [audioStates, setAudioStates] = useState({}); // Track state for each audio item
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const audioRef = useRef(null);
  const currentPlayingRef = useRef(null);

  const togglePlay = useCallback(
    async (item) => {
      const itemKey = item[0]; // Use date as unique key
      const currentState = audioStates[itemKey] || "idle";

      // If currently playing this item, pause it
      if (currentState === "playing" && audioRef.current) {
        audioRef.current.pause();
        setAudioStates((prev) => ({
          ...prev,
          [itemKey]: "idle",
        }));
        currentPlayingRef.current = null;
        return;
      }

      // Set loading state for the new item
      setAudioStates((prev) => ({
        ...prev,
        [itemKey]: "loading",
      }));

      try {
        const headers = { headers: { "Content-Type": "application/json" } };
        const requestData = {
          task: "getHealthlogSpeechPresignedURL",
          patient_id,
          date: item[0],
        };
        const res = await axios.post(
          Api.TELEDOC_PROVIDER_API,
          requestData,
          headers
        );
        const url = res.data;

        // Stop any currently playing audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }

        // Reset all audio states to idle
        setAudioStates((prev) => {
          const newStates = {};
          Object.keys(prev).forEach((key) => {
            newStates[key] = "idle";
          });
          newStates[itemKey] = "loading";
          return newStates;
        });

        // Create new audio instance
        audioRef.current = new Audio(url);
        currentPlayingRef.current = itemKey;
        setAudioPath(url);

        // Set up event listeners
        audioRef.current.addEventListener("canplay", () => {
          if (currentPlayingRef.current === itemKey) {
            setAudioStates((prev) => ({
              ...prev,
              [itemKey]: "playing",
            }));
          }
        });

        audioRef.current.addEventListener("ended", () => {
          if (currentPlayingRef.current === itemKey) {
            setAudioStates((prev) => ({
              ...prev,
              [itemKey]: "idle",
            }));
            currentPlayingRef.current = null;
          }
        });

        audioRef.current.addEventListener("error", () => {
          if (currentPlayingRef.current === itemKey) {
            setAudioStates((prev) => ({
              ...prev,
              [itemKey]: "idle",
            }));
            currentPlayingRef.current = null;
          }
        });

        await audioRef.current.play();
      } catch (error) {
        console.error("Error playing audio:", error);
        setAudioStates((prev) => ({
          ...prev,
          [itemKey]: "idle",
        }));
        currentPlayingRef.current = null;
      }
    },
    [patient_id]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Helper function to get button content based on state
  const getButtonContent = (item) => {
    const state = audioStates[item[0]] || "idle";

    switch (state) {
      case "loading":
        return <CircularProgress size={20} />;
      case "playing":
        return <PauseIcon />;
      default:
        return <PlayArrowIcon />;
    }
  };

  // Helper function to check if button should be disabled
  const isButtonDisabled = (item) => {
    const state = audioStates[item[0]] || "idle";
    return state === "loading" || state === "playing";
  };
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          p: 2,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError)
    return <Typography color="error">Error: {error.message}</Typography>;

  const [prescriptions = [], speech = []] = data;

  // Mobile card view for speech items
  const MobileSpeechView = () => (
    <Box sx={{ mt: 2 }}>
      {speech.map((item) => (
        <Card
          key={item[0]}
          sx={{
            mb: 2,
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
            },
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1, pr: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {item[0]}
                </Typography>
                <Typography variant="body1">{item[1]}</Typography>
              </Box>
              <IconButton
                aria-label="play audio"
                onClick={() => togglePlay(item)}
                disabled={isButtonDisabled(item)}
                sx={{
                  flexShrink: 0,
                  color: isButtonDisabled(item)
                    ? "text.disabled"
                    : "primary.main",
                  "&:hover": {
                    backgroundColor: isButtonDisabled(item)
                      ? "transparent"
                      : "primary.light",
                    color: isButtonDisabled(item) ? "text.disabled" : "white",
                  },
                }}
              >
                {getButtonContent(item)}
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  // Desktop table view for speech items
  const DesktopSpeechView = () => (
    <TableContainer
      component={Paper}
      sx={{
        mt: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Table sx={{ minWidth: 650 }}>
        <TableHead sx={{ backgroundColor: "action.hover" }}>
          <TableRow>
            <TableCell>
              <strong>Date</strong>
            </TableCell>
            <TableCell>
              <strong>CC</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Play</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {speech.map((item) => (
            <TableRow key={item[0]} hover>
              <TableCell>{item[0]}</TableCell>
              <TableCell>{item[1]}</TableCell>
              <TableCell align="center">
                <IconButton
                  aria-label="play audio"
                  onClick={() => togglePlay(item)}
                  disabled={isButtonDisabled(item)}
                  sx={{
                    color: isButtonDisabled(item) ? "text.disabled" : "inherit",
                  }}
                >
                  {getButtonContent(item)}
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 }, // Responsive padding
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Typography
        variant={isMobile ? "h6" : "h5"}
        gutterBottom
        sx={{
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          fontWeight: "bold",
        }}
      >
        Prescription CC Items
      </Typography>

      <List sx={{ width: "100%" }}>
        {prescriptions.map(([date, cc], idx) => (
          <ListItem
            key={idx}
            divider
            sx={{
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              py: { xs: 2, sm: 1 },
              px: { xs: 1, sm: 2 },
            }}
          >
            <ListItemText
              primary={cc}
              secondary={date}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                },
                "& .MuiListItemText-secondary": {
                  fontSize: { xs: "0.85rem", sm: "0.875rem" },
                  mt: { xs: 0.5, sm: 0 },
                },
              }}
            />
          </ListItem>
        ))}
      </List>

      <Typography
        variant={isMobile ? "h6" : "h5"}
        gutterBottom
        sx={{
          mt: 3,
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          fontWeight: "bold",
        }}
      >
        Speech CC Items
      </Typography>

      {isMobile ? <MobileSpeechView /> : <DesktopSpeechView />}
    </Box>
  );
};

export default ComplaintsList;
